import { c as _c } from "react/compiler-runtime";
import { cn } from "@repo/common/utils/cn";
import { mixpanelLabel, track } from "../../lib/mixpanel-client";

// ui
import { Body } from "@/components/ui/text";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AtSign, Clipboard, Copy, ExternalLink, Globe, Mail, MapPin, MessageCircle, Phone, PhoneCall } from "lucide-react";
import { toast } from "@/components/ui/sonner";

// hooks
import useCopyToClipboard from "react-use/lib/useCopyToClipboard";

// types
import { PublicCompany } from "@/components/company/types";
const getContactIconClasses = ({
  invert
}: {
  invert?: boolean;
}) => `${invert ? "bg-blue-900/20 hover:bg-blue-800/30" : "bg-gray-50 hover:bg-blue-200"} p-1 rounded-sm ${invert ? "text-gray-300 hover:text-blue-300" : "text-gray-400 hover:text-blue-500"} opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer`;
export type ContactItem = {
  icon: React.ReactNode;
  content: string;
  href: string;
  actionIcon: React.ReactNode;
  copyValue: string;
  tooltipSecondary: string;
  tooltipPrimary: string;
  alignStart?: boolean;
};
export type ContactCardProps = {
  company: Omit<PublicCompany, "isBanned">;
  invert?: boolean;
  showMobileActions?: boolean;
};
export function ContactCard(t0) {
  const $ = _c(33);
  const {
    company,
    invert,
    showMobileActions: t1
  } = t0;
  const showMobileActions = t1 === undefined ? true : t1;
  const [, copyToClipboard] = useCopyToClipboard();
  const resolveCompanyUrl = _temp;
  let handleLinkClick;
  let mobileActions;
  let t2;
  let t3;
  if ($[0] !== company.address || $[1] !== company.email || $[2] !== company.mobile || $[3] !== company.phone || $[4] !== company.www || $[5] !== copyToClipboard || $[6] !== invert) {
    const companyUrl = resolveCompanyUrl(company.www);
    const getContactItems = () => [company?.www && {
      icon: <Globe size={16} />,
      content: "Website",
      href: companyUrl,
      actionIcon: <ExternalLink size={16} />,
      copyValue: companyUrl,
      tooltipSecondary: "Website",
      tooltipPrimary: "Go to website"
    }, company?.email && {
      icon: <AtSign size={16} />,
      content: company.email,
      href: `mailto:${company.email}`,
      actionIcon: <Mail size={16} />,
      copyValue: company.email,
      tooltipSecondary: "Email address",
      tooltipPrimary: "Email"
    }, company?.mobile && {
      icon: <Phone size={16} />,
      content: company.mobile,
      href: `tel:${company.mobile || company.phone}`,
      actionIcon: <PhoneCall size={16} />,
      copyValue: company.mobile || company.phone,
      tooltipSecondary: "Phone number",
      tooltipPrimary: "Call phone number"
    }, company?.address && {
      icon: <MapPin size={16} />,
      content: [company.address.street1, company.address.street2, `${company.address.city}, ${company.address.state} ${company.address.zip}`].filter(Boolean).join("\n"),
      href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([company.address.street1, company.address.street2, `${company.address.city}, ${company.address.state} ${company.address.zip}`].filter(Boolean).join(" "))}`,
      actionIcon: <ExternalLink size={16} />,
      copyValue: `${company.address.street1} ${company.address.street2} ${company.address.city}, ${company.address.state} ${company.address.zip}`,
      tooltipSecondary: "Address",
      tooltipPrimary: "View on map",
      alignStart: true
    }].filter(Boolean) as ContactItem[];
    let t4;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Phone size={16} />;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    const t5 = `tel:${company?.mobile || company.phone}`;
    let t6;
    if ($[12] !== t5) {
      t6 = {
        icon: t4,
        label: "Call",
        href: t5
      };
      $[12] = t5;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <MessageCircle size={16} />;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    const t8 = `sms:${company?.mobile}`;
    let t9;
    if ($[15] !== t8) {
      t9 = {
        icon: t7,
        label: "Text",
        href: t8
      };
      $[15] = t8;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    let t10;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <Mail size={16} />;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    const t11 = `mailto:${company?.email}`;
    let t12;
    if ($[18] !== t11) {
      t12 = {
        icon: t10,
        label: "Email",
        href: t11
      };
      $[18] = t11;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    let t13;
    if ($[20] !== t12 || $[21] !== t6 || $[22] !== t9) {
      t13 = [t6, t9, t12];
      $[20] = t12;
      $[21] = t6;
      $[22] = t9;
      $[23] = t13;
    } else {
      t13 = $[23];
    }
    mobileActions = t13;
    handleLinkClick = _temp2;
    const renderContactItem = t14 => {
      const {
        icon,
        content,
        href,
        actionIcon,
        copyValue,
        tooltipSecondary,
        tooltipPrimary,
        alignStart: t15
      } = t14;
      const alignStart = t15 === undefined ? false : t15;
      return <div className={`flex gap-2 group ${alignStart ? "items-start" : "items-center"}`}><span className={`${invert ? "text-[#25457A] group-hover:text-blue-300" : "text-gray-400 group-hover:text-blue-500"}`}>{icon}</span><Body className={`text-left ${invert ? "text-gray-200 group-hover:text-white" : "text-gray-700 group-hover:text-foreground"} whitespace-pre-line leading-3`}><a href={href} target="_blank" rel="noreferrer noopener" className={cn(alignStart && "leading-tight")} onClick={() => handleLinkClick(tooltipSecondary)}>{content}</a></Body><TooltipProvider><Tooltip><TooltipTrigger asChild={true}><a href={href} target="_blank" rel="noreferrer noopener" className={getContactIconClasses({
                invert
              })} onClick={() => handleLinkClick(tooltipSecondary)}>{actionIcon}</a></TooltipTrigger><TooltipContent>{tooltipPrimary}</TooltipContent></Tooltip></TooltipProvider><TooltipProvider><Tooltip><TooltipTrigger asChild={true}><div className={getContactIconClasses({
                invert
              })} onClick={() => {
                copyToClipboard(copyValue ?? "");
                toast(<div className="flex items-center gap-2"><Clipboard size={16} />{`${tooltipSecondary} copied to clipboard`}</div>);
              }}><Copy size={16} /></div></TooltipTrigger><TooltipContent>{`Copy ${tooltipSecondary.toLowerCase()}`}</TooltipContent></Tooltip></TooltipProvider></div>;
    };
    t2 = "flex flex-col gap-3";
    t3 = getContactItems().map((item, index) => <div key={index} className="flex flex-col gap-2">{renderContactItem(item)}</div>);
    $[0] = company.address;
    $[1] = company.email;
    $[2] = company.mobile;
    $[3] = company.phone;
    $[4] = company.www;
    $[5] = copyToClipboard;
    $[6] = invert;
    $[7] = handleLinkClick;
    $[8] = mobileActions;
    $[9] = t2;
    $[10] = t3;
  } else {
    handleLinkClick = $[7];
    mobileActions = $[8];
    t2 = $[9];
    t3 = $[10];
  }
  let t4;
  if ($[24] !== handleLinkClick || $[25] !== invert || $[26] !== mobileActions || $[27] !== showMobileActions) {
    t4 = showMobileActions && <div className="flex md:hidden gap-1 pt-4 items-center" data-testid="mobile-actions">{mobileActions.map((t5, index_0) => {
        const {
          icon: icon_0,
          label,
          href: href_0
        } = t5;
        return <div key={index_0} className={`flex w-full justify-center items-center gap-2 p-4 rounded-md ${invert ? "bg-blue-900/20" : "bg-gray-100"}`}><a href={href_0} target="_blank" rel="noreferrer noopener" className="flex items-center gap-1" onClick={() => handleLinkClick(label)}>{icon_0}{label}</a></div>;
      })}</div>;
    $[24] = handleLinkClick;
    $[25] = invert;
    $[26] = mobileActions;
    $[27] = showMobileActions;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  let t5;
  if ($[29] !== t2 || $[30] !== t3 || $[31] !== t4) {
    t5 = <div className={t2}>{t3}{t4}</div>;
    $[29] = t2;
    $[30] = t3;
    $[31] = t4;
    $[32] = t5;
  } else {
    t5 = $[32];
  }
  return t5;
}
function _temp2(eventObject) {
  track(mixpanelLabel("Estimate", eventObject, "Clicked"));
}
function _temp(url) {
  if (!url) {
    return "";
  }
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  return `https://${url}`;
}